import { forwardRef } from 'react';

import { Select, SelectItem } from '@heroui/react';
import type { SelectProps } from '@heroui/react';

type SelectOption = {
  value: string;
  label: string;
  avatar?: string;
  isCustom?: boolean;
  customContent?: React.ReactNode;
  additionalInfo?: React.ReactNode;
};

type CustomSelectProps = Omit<SelectProps, 'children' | 'classNames'> & {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  error?: string;
};

export const CustomSelect = forwardRef<HTMLSelectElement, CustomSelectProps>(
  ({ label, placeholder, options, error, className, ...props }, ref) => {
    const getSelectedOption = (value: string | undefined) => {
      if (!value) return null;
      return options.find((option) => option.value === value);
    };

    return (
      <div>
        {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
        <Select
          ref={ref}
          placeholder={placeholder}
          variant="bordered"
          aria-label={label || placeholder || 'Select option'}
          className={`cursor-pointer ${className || ''}`}
          classNames={{
            trigger: [
              'bg-white/80',
              'backdrop-blur-sm',
              'border-gray-200',
              'hover:border-gray-300',
              'focus:border-violet-400',
              'data-[hover=true]:border-violet-400',
              'data-[open=true]:border-violet-400',
              'data-[focus=true]:border-violet-400',
              '!cursor-pointer',
              '!rounded-xl',
              'text-gray-800',
              'data-[hover=true]:bg-white/90',
              'min-h-[44px]',
              'flex',
              'items-center',
              'pl-10',
              'pr-8',
              'relative',
              '!overflow-visible',
              'shadow-sm',
              'hover:shadow-md',
              'transition-all',
              'duration-200',
            ],
            value: [
              'text-gray-800',
              'font-normal',
              'flex-1',
              'text-left',
              '!overflow-visible',
              '!w-full',
              '!max-w-none',
            ],
            selectorIcon: [
              'text-gray-500',
              'w-5',
              'h-5',
              'flex-shrink-0',
              'absolute',
              'left-3',
              'top-1/2',
              '-translate-y-1/2',
            ],
            popoverContent: [
              'bg-white',
              'border-gray-200',
              'rounded-xl',
              '!w-full',
              'min-w-full',
              'shadow-xl',
            ],
            base: ['!p-0', '!m-0'],
            mainWrapper: ['!min-h-[44px]'],
            innerWrapper: ['flex', 'items-center', 'justify-between', '!px-0', '!overflow-visible'],
          }}
          isInvalid={!!error}
          renderValue={(items) => {
            const selectedItem = items[0];
            if (!selectedItem) return placeholder;

            const selectedOption = getSelectedOption(selectedItem.key as string);
            if (!selectedOption) return placeholder;

            return (
              <div className="flex items-center" style={{ gap: '10px' }}>
                <span className="text-gray-800 font-medium">{selectedOption.label}</span>
                {selectedOption.avatar && (
                  <img
                    src={selectedOption.avatar}
                    alt=""
                    className="w-6 h-6 rounded-full object-cover"
                  />
                )}
              </div>
            );
          }}
          {...props}
        >
          {options.map((option) => (
            <SelectItem
              key={option.value}
              textValue={option.label}
              className="text-gray-800 data-[hover=true]:bg-violet-100 data-[hover=true]:rounded-lg data-[selected=true]:bg-violet-500 data-[selected=true]:text-white data-[selected=true]:rounded-lg"
              hideSelectedIcon={true}
            >
              {option.isCustom && option.customContent ? (
                option.customContent
              ) : (
                <div className="flex items-center" style={{ gap: '10px' }}>
                  <span className="font-medium">{option.label}</span>
                  {option.avatar && (
                    <img src={option.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                  )}
                </div>
              )}
            </SelectItem>
          ))}
        </Select>
      </div>
    );
  },
);

CustomSelect.displayName = 'CustomSelect';
